import React from 'react';
import { Grid2X2, Plus } from 'lucide-react';

import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { HIERARCHY_CHART_WIZARD } from '../../domains/analytics/constants/MetricEvents';
import { Tracker } from '../../domains/analytics/services/Tracker';

export type HierarchyChartWizardChoice = 'blank' | 'org-chart' | 'tree-diagram' | 'work-breakdown';

interface HierarchyChartWizardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectChoice: (choice: HierarchyChartWizardChoice) => void;
}

const choices: Array<{
  id: HierarchyChartWizardChoice;
  title: string;
  subtitle: string;
  preview: React.ReactNode;
}> = [
  {
    id: 'blank',
    title: 'Blank canvas',
    subtitle: 'Build from scratch',
    preview: (
      <div className="flex h-full items-center justify-center rounded-[18px] border-2 border-dashed border-[#3d9df2] bg-white">
        <Plus className="size-16 stroke-[1.7] text-[#2259d7]" aria-hidden />
      </div>
    ),
  },
  {
    id: 'org-chart',
    title: 'Org chart',
    subtitle: 'Roles and reporting lines',
    preview: (
      <div className="relative h-full overflow-hidden rounded-[18px] bg-gradient-to-br from-[#EEF6FF] to-[#E5F1FF]">
        <div className="absolute left-1/2 top-[22%] h-[19%] w-[30%] -translate-x-1/2 rounded-[8px] bg-gradient-to-br from-[#1F77F1] to-[#165ECD] shadow-[0_12px_28px_rgba(31,119,241,0.24)]" />
        <div className="absolute left-1/2 top-[41%] h-[16%] w-[3px] -translate-x-1/2 bg-[#1F66D4]" />
        <div className="absolute left-[21%] right-[21%] top-[56%] h-[3px] bg-[#1F66D4]" />
        <div className="absolute left-[21%] top-[56%] h-[16%] w-[3px] bg-[#1F66D4]" />
        <div className="absolute right-[21%] top-[56%] h-[16%] w-[3px] bg-[#1F66D4]" />
        <div className="absolute bottom-[22%] left-[10%] h-[16%] w-[26%] rounded-[8px] bg-gradient-to-br from-[#BFD8FF] to-[#A8C8FA]" />
        <div className="absolute bottom-[22%] right-[10%] h-[16%] w-[26%] rounded-[8px] bg-gradient-to-br from-[#BFD8FF] to-[#A8C8FA]" />
      </div>
    ),
  },
  {
    id: 'tree-diagram',
    title: 'Tree diagram',
    subtitle: 'Parent and child branches',
    preview: (
      <div className="relative h-full overflow-hidden rounded-[18px] bg-gradient-to-br from-[#ECFFF8] to-[#DFF6EF]">
        <div className="absolute left-[13%] top-[22%] h-[19%] w-[23%] rounded-[8px] bg-gradient-to-br from-[#28B879] to-[#159462] shadow-[0_12px_28px_rgba(21,148,98,0.18)]" />
        <div className="absolute left-[25%] top-[41%] h-[34%] w-[3px] bg-[#087A58]" />
        <div className="absolute left-[25%] top-[50%] h-[3px] w-[34%] bg-[#087A58]" />
        <div className="absolute left-[25%] top-[68%] h-[3px] w-[34%] bg-[#087A58]" />
        <div className="absolute right-[15%] top-[42%] h-[17%] w-[36%] rounded-[8px] bg-gradient-to-br from-[#BCEBD6] to-[#9BDFBE]" />
        <div className="absolute right-[15%] top-[61%] h-[17%] w-[36%] rounded-[8px] bg-gradient-to-br from-[#BCEBD6] to-[#9BDFBE]" />
      </div>
    ),
  },
  {
    id: 'work-breakdown',
    title: 'Work breakdown',
    subtitle: 'Deliverables and tasks',
    preview: (
      <div className="relative h-full overflow-hidden rounded-[18px] bg-gradient-to-br from-[#F3F1FF] to-[#ECE9FF]">
        <div className="absolute left-1/2 top-[22%] h-[19%] w-[30%] -translate-x-1/2 rounded-[8px] bg-gradient-to-br from-[#7A5BF5] to-[#6338D7] shadow-[0_12px_28px_rgba(99,56,215,0.22)]" />
        <div className="absolute left-1/2 top-[41%] h-[18%] w-[3px] -translate-x-1/2 bg-[#5144CF]" />
        <div className="absolute left-[17%] right-[17%] top-[58%] h-[3px] bg-[#5144CF]" />
        <div className="absolute left-[17%] top-[58%] h-[16%] w-[3px] bg-[#5144CF]" />
        <div className="absolute left-1/2 top-[58%] h-[16%] w-[3px] -translate-x-1/2 bg-[#5144CF]" />
        <div className="absolute right-[17%] top-[58%] h-[16%] w-[3px] bg-[#5144CF]" />
        <div className="absolute bottom-[21%] left-[6%] h-[16%] w-[23%] rounded-[8px] bg-gradient-to-br from-[#D3C9FF] to-[#BDAEF6]" />
        <div className="absolute bottom-[21%] left-1/2 h-[16%] w-[23%] -translate-x-1/2 rounded-[8px] bg-gradient-to-br from-[#D3C9FF] to-[#BDAEF6]" />
        <div className="absolute bottom-[21%] right-[6%] h-[16%] w-[23%] rounded-[8px] bg-gradient-to-br from-[#D3C9FF] to-[#BDAEF6]" />
      </div>
    ),
  },
];

export const HierarchyChartWizardDialog: React.FC<HierarchyChartWizardDialogProps> = ({
  open,
  onOpenChange,
  onSelectChoice,
}) => {
  const didTrackViewedRef = React.useRef(false);

  React.useEffect(() => {
    if (!open || didTrackViewedRef.current) {
      return;
    }

    didTrackViewedRef.current = true;
    Tracker.track(HIERARCHY_CHART_WIZARD.VIEWED);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'z-[1001] gap-0 border border-[#E8ECF3] bg-white p-0 shadow-[0_24px_80px_rgba(15,23,42,0.12)]',
          'w-[min(1280px,calc(100vw-32px))] max-h-[calc(100vh-32px)] overflow-y-auto rounded-[22px]',
          'sm:max-w-[1180px]',
        )}
        overlayClassName="z-[1000] bg-[#F8FAFD]/90"
        showCloseButton={false}
        onPointerDownOutside={(event) => event.preventDefault()}
      >
        <div className="px-8 py-10 md:px-10 lg:px-10 lg:pb-10 lg:pt-10">
          <DialogTitle className="font-['Inter'] text-[32px] font-semibold leading-tight tracking-normal text-[#070D22]">
            Create a Hierarchy Chart
          </DialogTitle>
          <p className="mt-2 font-['Inter'] text-[18px] font-normal leading-tight text-[#667085]">
            Start from scratch or choose a professionally designed template to map any hierarchical structure.
          </p>

          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
            {choices.map((choice) => (
              <button
                key={choice.id}
                type="button"
                onClick={() => onSelectChoice(choice.id)}
                className={cn(
                  'flex min-h-[290px] flex-col rounded-[18px] border border-[#E3E7EE] bg-white p-6 text-left shadow-[0_16px_40px_rgba(15,23,42,0.08)] transition',
                  'hover:-translate-y-0.5 hover:border-[#C9D6F8] hover:shadow-[0_22px_52px_rgba(15,23,42,0.12)]',
                  'focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-[#2259D7] focus-visible:ring-offset-0',
                )}
                data-testid={`tid.hierarchy-chart-wizard.${choice.id}`}
              >
                <div className="h-[180px] w-full">{choice.preview}</div>
                <span className="mt-4 font-['Inter'] text-[18px] font-medium leading-none text-[#080D1F]">
                  {choice.title}
                </span>
                <span className="mt-2 font-['Inter'] text-[16px] font-normal leading-none text-[#667085]">
                  {choice.subtitle}
                </span>
              </button>
            ))}
          </div>

          <div className="mt-10 flex justify-center">
            <Button
              type="button"
              onClick={() => {
                Tracker.track(HIERARCHY_CHART_WIZARD.TEMPLATE_STARTED);
                onOpenChange(false);
              }}
              className="h-[60px] rounded-[10px] bg-gradient-to-br from-[#2259D7] to-[#1C55D8] px-14 font-['Inter'] text-[18px] font-normal text-white shadow-[0_18px_42px_rgba(34,89,215,0.28)] hover:from-[#1E4FC4] hover:to-[#174BC6]"
              data-testid="tid.hierarchy-chart-wizard.start-template"
            >
              <Grid2X2 className="size-6" aria-hidden />
              Start from a template
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
